// useRefetchOnWindowFocus — hält Editor-Fenster und Katalog-Zweitfenster auf demselben
// Deck synchron. Das globale `staleTime` (5 min, `queryClient.js`) unterdrückt den
// eingebauten Fokus-Refetch von React Query, also holt dieser Hook beim Zurückkehren in
// den Tab die Deck-, Wantlist- und Kategorie-Queries gezielt per Invalidierung frisch —
// sonst zeigt das jeweils andere Fenster eine gerade drüben hinzugefügte Karte bis zu
// 5 Minuten lang nicht. Das Netz unter `useDeckSyncChannel`: greift der Fenster-Abgleich
// nicht (fehlender BroadcastChannel, verpasste Meldung), holt spätestens der Fokus frisch —
// dann bleibt allerdings die Vorschlagsliste bis zur nächsten eigenen Mutation auf dem
// alten Stand, denn der Fokus-Refetch holt sie nicht neu (Spec §6.4).
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateDeckViews } from './deckViewInvalidation';

/**
 * In BEIDEN Fenstern mounten (Editor-Route und Katalog-Seite).
 *
 * @param {number|null} [numericDeckId] - numerische Deck-ID für die Kategorie-Query;
 *   ohne sie (Deck noch nicht geladen) bleibt nur diese eine Invalidierung aus.
 */
export function useRefetchOnWindowFocus(numericDeckId) {
  const queryClient = useQueryClient();
  // Nur ein 'focus' NACH einem 'blur' zählt als Fensterwechsel — ein Browser feuert
  // 'focus' nicht beim ersten Laden der Seite, aber Vorsicht ist trotzdem eingebaut,
  // damit kein Fokus-Wackeln (z.B. ein Popover) ungewollt einen Refetch auslöst.
  const blurredRef = useRef(false);

  useEffect(() => {
    const handleBlur = () => { blurredRef.current = true; };
    const handleFocus = () => {
      if (!blurredRef.current) return;
      blurredRef.current = false;
      invalidateDeckViews(queryClient, numericDeckId);
    };
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [queryClient, numericDeckId]);
}

export default useRefetchOnWindowFocus;
